import { DatePicker } from "@portal/BaseComponents/DatePicker/AntDatePicker";
import { Flex } from "@portal/BaseComponents/Flex/Flex";
import { Form, useForm } from "@portal/BaseComponents/Form/AntForm";

import { FormRules } from "@portal/BaseComponents/Form/type";
import { InputNumber, TextArea } from "@portal/BaseComponents/Input/AntInput";
import {
  OnInputNumberChange,
  OnTextAreaChange,
} from "@portal/BaseComponents/Input/type";
import { SimpleTableColumn } from "@portal/BaseComponents/Table/SimpleTable/type";
import {
  Link,
  TypographyText,
} from "@portal/BaseComponents/Typography/AntTypography";
import { Invoice } from "@portal/types/entities/invoice";
import { getCurrencySymbol } from "@portal/util/number-formatter";

import { StyledFormItem } from "@portal/BaseComponents/Form/style";
import { AmountText } from "@portal/BaseComponents/Table/Components/Amount/AmountText";
import { useFormats } from "@portal/hooks/use-formats";
import { dateFormats } from "@sinecycle/growcomponents";
import dayjs from "dayjs";
import { subtract } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { AttributeTable, EmptyPlaceHolder } from "./style";
import {
  PTPAmountAttributeProps,
  PTPDefaultFormElementProps,
  PaymentAmountProps,
} from "./type";
// const t = {
//   total_amount: "Total Payment Amount",
//   remove_invoice: "Remove Invoice",
//   error_help: [
//     "If you have received a payment for existing PTPs, please close them ",
//     "If current PTP amount is part of an existing PTP, delete existing PTP ",
//     "If you want to re vise PTP date of an existing PTP, please record payment slippage",
//   ],
// };

function PTPAmountAttribute(props: PTPAmountAttributeProps<string, string>) {
  const { invoiceList, onAmountChange } = props;

  const columns: SimpleTableColumn<Invoice> = [
    {
      key: "Invoice",
      title() {
        return <TypographyText strong>Invoice ID</TypographyText>;
      },
      render(value: Invoice["number"]) {
        return <Link>{value}</Link>;
      },
      dataIndex: "number",
    },
    {
      key: "Balance",
      title() {
        return <TypographyText strong>Current Balance</TypographyText>;
      },
      render(value: Invoice["balance_due_amount"], record) {
        return (
          <AmountText amount={value} currency={record.currency} format="FULL" />
        );
      },
      dataIndex: "balance_due_amount",
      align: "right",
    },
    {
      key: "PTP Amount",
      title() {
        return <TypographyText strong>Existing PTP Amount</TypographyText>;
      },
      render(value: Invoice["ptp_balance_amount"], record) {
        return (
          <AmountText
            amount={value ?? 0}
            currency={record.currency}
            format="FULL"
          />
        );
      },
      align: "right",
      dataIndex: "ptp_balance_amount",
    },
    {
      key: "Payment Amount",
      title() {
        return <TypographyText strong>Payment Amount</TypographyText>;
      },
      render(value, record) {
        const amount = subtract(
          record.balance_due_amount,
          record.ptp_balance_amount ?? 0
        );

        function handleAmountChange(amount: number) {
          onAmountChange(record.id, amount);
        }

        return (
          <PaymentAmount
            amount={amount}
            currency={record.currency}
            onAmountChange={handleAmountChange}
            formName={record.id}
          />
        );
      },
      align: "right",
    },
  ];

  function handleShowHeader() {
    return Boolean(invoiceList?.length);
  }

  const isData = useMemo(handleShowHeader, [invoiceList?.length]);

  const noData = (
    <EmptyPlaceHolder justify="center" align="center">
      Add Invoices to set payment amount
    </EmptyPlaceHolder>
  );
  return (
    <AttributeTable
      design={isData ? "data" : "empty"}
      showHeader={isData}
      columns={columns}
      dataSource={invoiceList}
      pagination={false}
      style={{
        boxShadow: isData ? "var(--shadow-2)" : "none",
        border: isData ? "1px solid var(--gray-4)" : "0",
        borderRadius: "var(--br-1)",
      }}
      scroll={{ y: 150 }}
      locale={{
        emptyText() {
          return noData;
        },
      }}
      rowKey={(record) => record.id}
      // footer={(value) => {
      //   return isData ? (
      //     <Flex justify="flex-end" style={{ width: "100%" }}>
      //       <Flex justify="space-between" style={{ width: "50%" }}>
      //         <TypographyText strong>{t.total_amount}</TypographyText>
      //         <AmountText
      //           amount={totalAmount ?? 0}
      //           currency={value[0].currency}
      //           format="FULL"
      //           textProps={{ strong: true }}
      //         />
      //       </Flex>
      //     </Flex>
      //   ) : false;
      // }}
    />
  );
}

function PTPDateForm(
  props: PTPDefaultFormElementProps<string, string | undefined>
) {
  const { value, onChange } = props;
  const {dateformat} = useFormats();
  return (
    <DatePicker
      format={dateformat}
      defaultValue={value ? dateFormats.dayJS(value) : undefined}
      onChange={(value) => {
        onChange?.(dayjs(value as any).toISOString());
      }}
      getPopupContainer={(node) => {
        return node.parentNode as HTMLElement;
      }}
    />
  );
}

function PTPNotesForm(props: PTPDefaultFormElementProps<string, string>) {
  const { value, onChange } = props;
  function handleChange(event: OnTextAreaChange[number]) {
    onChange?.(event.target.value);
  }
  return <TextArea defaultValue={value} onChange={handleChange} rows={4} />;
}

function PaymentAmount(props: Readonly<PaymentAmountProps>) {
  const { amount, currency, onAmountChange, formName } = props;
  const [form] = useForm();
  const [initialAmount] = useState(amount);
  const [, setIsError] = useState(amount === 0);
  const currencySymbol = getCurrencySymbol(currency);

  const validationRule: FormRules = [
    () => ({
      validator(_, value, callBack) {
        if (value <= 0) {
          try {
            setIsError(true);

            return Promise.reject(new Error(""));
          } catch (err) {
            callBack(err as any);
          }
        }
        if (value <= initialAmount) {
          try {
            setIsError(false);

            return Promise.resolve();
          } catch (err) {
            callBack(err as any);
          }
        }
        try {
          setIsError(true);

          return Promise.reject(new Error(""));
        } catch (err) {
          callBack(err as unknown as string);
        }
      },
    }),
  ];

  // function handleRemove() {
  //   onRemove?.();
  // }

  // const RemoveInvoice = (
  //   <Button size="small" type="primary" onClick={handleRemove}>
  //     {t.remove_invoice}
  //   </Button>
  // );

  function initialValidation() {
    if (amount === 0) {
      form.validateFields();
    }
  }

  useEffect(initialValidation, [amount, form]);
  /* The code is defining two functions: `Title` and `renderItem`. */
  // const Title = (
  //   <TypographyText style={{ color: "var(--red-3)" }}>Error(s)</TypographyText>
  // );
  // function renderItem(...args: ListRenderItem<string>) {
  //   const [value] = args;
  //   return (
  //     <Item>
  //       <TypographyText style={{ color: "var(--gray-1)" }}>
  //         {value}
  //       </TypographyText>
  //     </Item>
  //   );
  // }
  // const HelpText = (
  //   <List<string>
  //     header={Title}
  //     dataSource={t.error_help}
  //     // footer={RemoveInvoice}
  //     renderItem={renderItem}
  //   />
  // );

  function handleChange(value: OnInputNumberChange[number]) {
    onAmountChange(value as unknown as number);
  }

  return (
    <Flex align="center" justify="flex-end">
      <Form form={form} layout="vertical" name={formName} requiredMark={true}>
        <StyledFormItem
          name="payment_amount"
          rules={validationRule}
          initialValue={amount}
          fieldId="payment_amount"
          hide_error
        >
          <InputNumber
            prefix={currencySymbol}
            controls={false}
            width="140px"
            min={0}
            step="0.00"
            onChange={handleChange}
            disabled={amount === 0}
          />
        </StyledFormItem>
      </Form>
    </Flex>
  );
}

export { PTPAmountAttribute, PTPDateForm, PTPNotesForm, PaymentAmount };
